import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Form, Input, Steps, Typography, message } from "antd";
import { KeyOutlined, MailOutlined, SafetyOutlined } from "@ant-design/icons";
import type {
  LoginResponse,
  SetupTotpResponse,
  VerifyCodeResponse,
} from "@coco-mailrelay/shared-types";
import { apiRequest } from "../lib/apiRequest";
import { errorMessage } from "../lib/errors";
import { useAuthStore } from "../store/auth.store";
import TotpSetup from "../components/TotpSetup";

const { Title, Text } = Typography;

type Stage = "email" | "code" | "totp" | "totp_setup";

const LAST_EMAIL_KEY = "mailrelay_admin_last_email";

export default function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);

  const [stage, setStage] = useState<Stage>("email");
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [pendingToken, setPendingToken] = useState("");
  const [setup, setSetup] = useState<SetupTotpResponse | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [savedEmail] = useState(() => localStorage.getItem(LAST_EMAIL_KEY) ?? "");
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const startCountdown = (): void => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(60);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const finishLogin = (res: LoginResponse): void => {
    localStorage.setItem(LAST_EMAIL_KEY, email);
    login(res.token, res.member);
    message.success("Signed in");
    navigate("/tenants");
  };

  const sendCode = async (target: string): Promise<void> => {
    await apiRequest("/auth/send-code", { method: "POST", body: { email: target } });
  };

  const handleSendCode = async (values: { email: string }): Promise<void> => {
    setLoading(true);
    try {
      await sendCode(values.email);
      setEmail(values.email);
      setStage("code");
      startCountdown();
      message.success("A login code was sent to your email");
    } catch (err) {
      message.error(errorMessage(err, "Failed to send code"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (values: { code: string }): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiRequest<VerifyCodeResponse>("/auth/verify-code", {
        method: "POST",
        body: { email, code: values.code },
      });
      setPendingToken(res.pendingToken);
      if (res.step === "totp") {
        setStage("totp");
      } else {
        const setupRes = await apiRequest<SetupTotpResponse>("/auth/setup-totp", {
          method: "POST",
          authToken: res.pendingToken,
        });
        setSetup(setupRes);
        setPendingToken(setupRes.pendingToken);
        setStage("totp_setup");
      }
    } catch (err) {
      message.error(errorMessage(err, "Verification failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyTotp = async (values: { totpCode: string }): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiRequest<LoginResponse>("/auth/verify-totp", {
        method: "POST",
        body: { totpCode: values.totpCode },
        authToken: pendingToken,
      });
      finishLogin(res);
    } catch (err) {
      message.error(errorMessage(err, "Invalid code"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async (totpCode: string): Promise<void> => {
    setLoading(true);
    try {
      const res = await apiRequest<LoginResponse>("/auth/confirm-totp-setup", {
        method: "POST",
        body: { totpCode },
        authToken: pendingToken,
      });
      finishLogin(res);
    } catch (err) {
      message.error(errorMessage(err, "Setup failed"));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (countdown > 0) return;
    setLoading(true);
    try {
      await sendCode(email);
      startCountdown();
      message.success("Code resent");
    } catch (err) {
      message.error(errorMessage(err, "Failed to resend"));
    } finally {
      setLoading(false);
    }
  };

  const currentStep = stage === "email" ? 0 : stage === "code" ? 1 : 2;

  return (
    <div style={pageStyle}>
      <Card style={{ width: 420, borderRadius: 12 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <Title level={3} style={{ marginBottom: 4 }}>
            coco-mailrelay
          </Title>
          <Text type="secondary">Admin console</Text>
        </div>

        <Steps
          current={currentStep}
          size="small"
          style={{ marginBottom: 24 }}
          items={[
            { title: "Email" },
            { title: "Code" },
            { title: stage === "totp_setup" ? "Bind TOTP" : "TOTP" },
          ]}
        />

        {stage === "email" && (
          <Form layout="vertical" onFinish={handleSendCode} autoComplete="off" initialValues={{ email: savedEmail }}>
            <Form.Item
              name="email"
              rules={[
                { required: true, message: "Enter your email" },
                { type: "email", message: "Invalid email" },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="you@company.com" size="large" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Send login code
              </Button>
            </Form.Item>
          </Form>
        )}

        {stage === "code" && (
          <Form layout="vertical" onFinish={handleVerifyCode} autoComplete="off">
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <Text type="secondary">
                Code sent to <Text strong>{email}</Text>
              </Text>
            </div>
            <Form.Item
              name="code"
              rules={[
                { required: true, message: "Enter the code" },
                { len: 6, message: "Code must be 6 digits" },
              ]}
            >
              <Input
                prefix={<SafetyOutlined />}
                placeholder="6-digit code"
                size="large"
                maxLength={6}
                inputMode="numeric"
                style={{ letterSpacing: 4, textAlign: "center", fontSize: 18 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Verify
              </Button>
            </Form.Item>
            <div style={{ textAlign: "center" }}>
              <Button type="link" disabled={countdown > 0} onClick={handleResend}>
                {countdown > 0 ? `Resend (${countdown}s)` : "Resend code"}
              </Button>
              <Button type="link" onClick={() => setStage("email")}>
                Change email
              </Button>
            </div>
          </Form>
        )}

        {stage === "totp" && (
          <Form layout="vertical" onFinish={handleVerifyTotp} autoComplete="off">
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <Text type="secondary">Enter the 6-digit code from your authenticator app.</Text>
            </div>
            <Form.Item
              name="totpCode"
              rules={[
                { required: true, message: "Enter the code" },
                { len: 6, message: "Code must be 6 digits" },
              ]}
            >
              <Input
                prefix={<KeyOutlined />}
                placeholder="6-digit code"
                size="large"
                maxLength={6}
                inputMode="numeric"
                style={{ letterSpacing: 4, textAlign: "center", fontSize: 18 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={loading} block size="large">
                Verify and sign in
              </Button>
            </Form.Item>
          </Form>
        )}

        {stage === "totp_setup" && setup && (
          <TotpSetup
            otpauthUri={setup.otpauthUri}
            secret={setup.secret}
            loading={loading}
            submitLabel="Bind and sign in"
            onConfirm={handleConfirmSetup}
          />
        )}
      </Card>
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#f5f5f5",
};
